<script setup lang="ts">
import { computed } from 'vue'
import { Brand, Logo } from './types'

interface Props {
  selectedBrand: Brand | null
  isLoadingBrand: boolean
  selectedLogo?: Logo | null
}

interface Emits {
  (e: 'selectLogo', logo: Logo): void
}

type LogoGroups = Record<string, Logo[]>

const props = defineProps<Props>()
defineEmits<Emits>()

// Shows only one option per theme and type of logo, Example (SVG, dark, logo) or (SVG, light, symbol)
// It always tries to fetch the svg logo first, then webp, png, jpg
const logosToShow = computed<Logo[]>(() => {
  const logos = props.selectedBrand?.logos || []

  const logoGroups = logos.reduce((acc: LogoGroups, logo: Logo) => {
    const themeKey = `${logo.theme}-${logo.type}`
    if (!acc[themeKey]) {
      acc[themeKey] = []
    }
    acc[themeKey].push(logo)
    return acc
  }, {})

  return Object.entries(logoGroups)
    .map(([_key, logos]) => {
      const logosMap = Object.fromEntries(logos.map((logo) => [logo.format, logo]))

      return (
        logosMap['svg'] ||
        logosMap['webp'] ||
        logosMap['png'] ||
        logosMap['jpg'] ||
        logosMap['jpeg']
      )
    })
    .filter(Boolean)
})
</script>

<template>
  <div class="brand-details">
    <!-- Loading State -->
    <div v-if="isLoadingBrand" class="py-3">
      <UeSpinner text="Loading brand details..." />
    </div>

    <!-- Brand Details Content -->
    <div v-else-if="selectedBrand" class="brand-content text-left">
      <!-- Logos List -->
      <div class="mt-4" v-if="selectedBrand.logos && selectedBrand.logos.length > 0">
        <h6>Available Logos</h6>
        <div class="logos-grid mt-3">
          <div
            v-for="(logo, index) in logosToShow"
            :key="index"
            class="logo-item rounded p-3 mb-2"
            :class="{ 'logo-item--selected': selectedLogo === logo }"
            @click="$emit('selectLogo', logo)"
          >
            <div class="d-flex align-items-center">
              <img
                :src="logo.src"
                :alt="`${selectedBrand.name} logo`"
                :class="[
                  'logo-preview',
                  'p-2',
                  'rounded-sm',
                  'border',
                  'mr-4',
                  { 'logo-preview__dark': logo.theme === 'light' },
                ]"
              />
              <div class="flex-grow-1">
                <div class="small">
                  <span class="badge badge-secondary fs-xsm mr-1">{{
                    logo.format.toUpperCase()
                  }}</span>
                  <span v-if="logo.theme" class="badge badge-outline-secondary border fs-xsm mr-1">
                    {{ logo.theme }}
                  </span>
                  <span v-if="logo.size" class="badge badge-outline-secondary border fs-xsm">
                    {{ logo.size }}px
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Logos -->
      <div v-else class="text-center py-3 text-muted">
        <p class="mb-0">No logos available for this brand</p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="sass">
.logo-item
  cursor: pointer

  &:hover, &--selected
    background-color: #E1C0FF26
    border: 1px solid var(--primary)

.logos-grid
  max-height: 400px
  overflow-y: auto

.logo-preview
  max-width: 100px
  object-fit: contain

  &__dark
    background-color: var(--ueBlack)
</style>
